import { useEffect, useState, useRef } from 'react';
import { Drawer, Input, Tag, Rate, message, Tabs } from 'antd';
import { drawerWidth } from '@utils/property';
import { getExpertAndAgendaInfoApi } from '@services/Conference';
import { getExpertEvalDimensionsApi, saveExpertEvalRecordApi, getExpertEvalRecordsApi } from '@services/Experts';
import { expertLevels, starTexts } from '@utils/constants';
import { convertDateTimeRangeToLocalText } from '@utils/utils';
import WorkExperiences from '@/components/common/WorkExperiences';

import '../expert_evaluation.less';

// 开始评分
const EvaluationCheckDrawer = props => {
    const { visibleEvaluationCheckDrawer, setVisibleEvaluationCheckDrawer, currentEvaluation } = props;

    const [expertInfo, setExpertInfo] = useState({}); // 用户信息
    const [agendaInfo, setAgendaInfo] = useState({}); // 日程任务信息
    const [expertEvalRecords, setExpertEvalRecords] = useState([]);

    const [tabActiveKey, setTabActiveKey] = useState(0);

    const onTabClick = key => setTabActiveKey(key);

    // 记录人切换
    const onChangeTabs = value => {
        console.log(value);
    };

    // 关闭查看弹窗
    const closeEvaluationCheckDrawer = () => {
        setVisibleEvaluationCheckDrawer(false);
        setTabActiveKey(0);
        setExpertEvalRecords([]);
    };

    // 获取专家和日程信息
    const getExpertAndAgendaInfo = async () => {
        const res = await getExpertAndAgendaInfoApi({ agendaId: currentEvaluation.agendaId, expertId: currentEvaluation.expertId });
        if (res.status_code === 200) {
            // console.log(res.data);
            setExpertInfo(res.data.expert);
            setAgendaInfo(res.data.agendaInfo);
        }
    };

    // 获取专家综评维度
    const getExpertEvalDimensions = async () => {
        const res = await getExpertEvalDimensionsApi();
        if (res.status_code === 200) {
            const datas = {
                合作友好度: [...res.data['合作友好度']],
                报告专业度: [...res.data['报告专业度']],
            };
            getExpertEvalRecords(datas);
        }
    };

    const getExpertEvalRecords = async dimensions => {
        const res = await getExpertEvalRecordsApi({ agendaId: currentEvaluation.agendaId, expertId: currentEvaluation.expertId, taskId: currentEvaluation.taskId });
        if (res.status_code === 200 && res.data) {
            // console.log(res.data);
            const datas = res.data;

            const updatedData = Object.keys(datas).map(key => {
                const records = datas[key].records;

                // Create a map of evalId to starNum for faster lookup
                const starNumMap = {};
                records.forEach(item => {
                    starNumMap[item.evalId] = item.starNum;
                });

                // Create updated dimensions with starNum values
                const updatedDimensions = { ...dimensions };
                Object.keys(updatedDimensions).forEach(dimKey => {
                    updatedDimensions[dimKey] = updatedDimensions[dimKey].map(ele => ({
                        ...ele,
                        starNum: starNumMap[ele.id] || ele.starNum,
                    }));
                });

                return {
                    ...datas[key],
                    dimensions: updatedDimensions,
                    userName: key,
                };
            });

            // console.log(updatedData, 'datas');
            setExpertEvalRecords(updatedData);
        }
    };

    useEffect(() => {
        if (!!visibleEvaluationCheckDrawer && !!currentEvaluation?.expertId) {
            getExpertAndAgendaInfo();
            getExpertEvalDimensions(); // 专家记录维度
        }
    }, [visibleEvaluationCheckDrawer]);

    return (
        <Drawer title="记录详情" size={drawerWidth} open={visibleEvaluationCheckDrawer} onClose={closeEvaluationCheckDrawer} layout="horizontal" mask={{ closable: false }} footer={null} closable={{ placement: 'end' }}>
            <div className="evaluation-container">
                <div className="evaluation-info">
                    <div className="expert-info">
                        <span>{expertInfo.memUsername}</span>
                        <span>{expertLevels.find(a => a.level === expertInfo.expertLevel)?.label}</span>
                        <span>
                            {!expertInfo?.memMobile || expertInfo?.memMobile?.startsWith('100') ? (
                                <Tag color="#f50" style={{ padding: 2, fontSize: 10, lineHeight: 1 }}>
                                    无手机号
                                </Tag>
                            ) : (
                                expertInfo?.memMobile
                            )}
                        </span>
                    </div>
                    <p>单位任职：</p>
                    <WorkExperiences workExperiences={expertInfo.ssoMemberWorkExperiencesEntities} layout="block" />

                    <div className="conference-info">
                        <h3>所属项目</h3>
                        <h2>{agendaInfo.name}</h2>
                        <h3>任务内容</h3>
                        <p>
                            {agendaInfo.title} <Tag color="blue">{agendaInfo.taskName}</Tag>
                        </p>
                        <span>{convertDateTimeRangeToLocalText(agendaInfo.start_time, agendaInfo.end_time, 0)}</span>
                    </div>
                </div>

                <div className="experts-container">
                    <div className="experts-total">
                        <h4>记录人（{expertEvalRecords.length}人）</h4>
                        <p>
                            <span>合作友好度：{agendaInfo.friendliness_score}分</span>
                            <span>报告专业度：{agendaInfo.profession_score}分</span>
                            <span>价值度：{agendaInfo.total_score}分</span>
                        </p>
                    </div>
                    <div className="experts">
                        <Tabs
                            onChange={onChangeTabs}
                            tabPlacement="start"
                            type="card"
                            size="large"
                            activeKey={tabActiveKey}
                            onTabClick={onTabClick}
                            items={
                                !!expertEvalRecords.length
                                && expertEvalRecords.map((item, index) => {
                                    return {
                                        label: item.userName,
                                        key: index,
                                        children: (
                                            <div className="expert-box">
                                                <p className="expert-date">记录时间：{item.createdDate.slice(0, -3)}</p>
                                                {!!Object.keys(item.dimensions)
                                                    && Object.keys(item.dimensions).map((keyname, index) => (
                                                        <div className="options-container" key={index}>
                                                            <h3 className="options-title">{keyname}</h3>
                                                            <div className="options">
                                                                {!!item.dimensions[keyname]
                                                                    && item.dimensions[keyname].map((val, idx) => (
                                                                        <div className="option" key={val.id}>
                                                                            <p>{val.name}</p>
                                                                            <Rate count={5} disabled value={val.starNum} className="custom-rate-check" />
                                                                            {val.starNum ? <span className="ant-rate-text">{starTexts[val.starNum - 1]}</span> : ''}
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                <div className="description-box">
                                                    <h3>特殊说明</h3>
                                                    <p>{item.remarks || '暂无'}</p>
                                                </div>
                                            </div>
                                        ),
                                    };
                                })
                            }
                        />
                    </div>
                </div>
            </div>
        </Drawer>
    );
};
export default EvaluationCheckDrawer;
